import React, { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { profile } from "../mock/mock";
import { MaskReveal, EASE } from "../components/Reveal";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const nameDrift = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const portraitDrift = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(useTransform(mx, [-1, 1], [12, -12]), { stiffness: 55, damping: 22 });
  const py = useSpring(useTransform(my, [-1, 1], [8, -8]), { stiffness: 55, damping: 22 });

  const onMouseMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 2);
    my.set((e.clientY / window.innerHeight - 0.5) * 2);
  };

  const scrollToStory = () => {
    const el = document.getElementById("story");
    if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.5 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-theme="dark"
      ref={ref}
      onMouseMove={onMouseMove}
      className="sec-dark relative h-[100svh] overflow-hidden"
      data-testid="home-hero"
    >
      {/* Local ambient glow */}
      <div
        aria-hidden="true"
        className="absolute -top-[20vh] -left-[10vw] w-[70vw] h-[85vh] pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at center, rgba(245,245,247,0.05), transparent 62%)",
          filter: "blur(48px)",
        }}
      />

      <motion.div
        style={{ scale: portraitScale, x: px, y: portraitDrift }}
        className="absolute inset-y-0 right-0 w-full md:w-[64%] lg:w-[60%] overflow-hidden"
      >
        <motion.img
          src={profile.portrait}
          alt={profile.name}
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          className="w-full h-full object-cover object-center portrait-bw"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/30 to-transparent md:from-bg md:via-bg/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-bg/70 via-transparent to-bg/10" />
      </motion.div>

      <motion.div
        style={{ y: nameDrift }}
        className="relative z-10 h-full flex items-center px-6 md:px-12"
      >
        <div className="w-full max-w-[1440px] mx-auto">
          <motion.div style={{ x: py }}>
            <div className="eyebrow mb-7 md:mb-9">
              <MaskReveal delay={0.15}>Global Health · New Delhi</MaskReveal>
            </div>
            <h1 className="name-hero mix-blend-difference text-ink md:max-w-[80%]">
              <span className="block"><MaskReveal delay={0.3}>Dr. Abhishek</MaskReveal></span>
              <span className="block"><MaskReveal delay={0.46}>Thakkar<span className="t-terra">.</span></MaskReveal></span>
            </h1>
            <div
              className="mt-8 md:mt-11 flex items-center gap-4 text-[13px] md:text-[14px] font-medium tracking-[0.02em] t-dim"
              data-testid="home-role"
            >
              <MaskReveal delay={0.9}>
                <span className="t-fg">Physician</span>
                <span className="mx-3 t-terra">·</span>
                <span className="t-fg">Global Health Strategist</span>
              </MaskReveal>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue — quiet vertical line with descending dot */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.5, ease: EASE }}
        onClick={scrollToStory}
        className="absolute bottom-10 left-6 md:left-12 z-10 flex flex-col items-center gap-4 group"
        data-testid="hero-scroll-cue"
        aria-label="Scroll to story"
      >
        <span className="text-[10px] tracking-[0.34em] uppercase t-dim group-hover:t-fg transition-colors [writing-mode:vertical-rl]">
          Scroll
        </span>
        <span className="scroll-cue-line" />
      </motion.button>
    </section>
  );
};

export default Hero;
